import { extractErrorMessage } from '@/lib'
import { useAuthForgotPassword } from '@jobilla/nexus-sdk'
import { Link } from '@tanstack/react-router'
import { isAxiosError } from 'axios'
import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { logger } from '../../utilities'
import { AuthErrorAlert } from './auth-error-alert'
import { MailIcon } from './auth-icons'
import { AuthInput } from './auth-input'
import { AuthSubmitButton } from './auth-submit-button'
import { JobillaBrandLogo } from './jobilla-brand-logo'

export function ForgotPasswordForm() {
	const { t } = useTranslation('common')
	const [email, setEmail] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [isSuccess, setIsSuccess] = useState(false)
	const forgotPassword = useAuthForgotPassword()

	async function handleRequestReset(event: FormEvent) {
		event.preventDefault()

		const trimmedEmail = email.trim()
		if (!trimmedEmail) {
			setError(t('auth.forgotPassword.errorEmptyEmail'))
			return
		}

		setError(null)

		try {
			await forgotPassword.mutateAsync({ data: { email: trimmedEmail } })
			setIsSuccess(true)
		} catch (err) {
			const status = isAxiosError(err) ? err.response?.status : undefined
			if (!status || status >= 500) {
				logger.error(err, { category: 'auth', data: { action: 'forgot-password' } })
			} else {
				logger.warn('Forgot password request failed', {
					category: 'auth',
					data: { status, action: 'forgot-password' },
				})
			}
			setError(extractErrorMessage(err, t('auth.forgotPassword.errorDefault')))
		}
	}

	if (isSuccess) {
		return (
			<div className="w-full max-w-md mx-auto">
				<JobillaBrandLogo />

				<div className="mt-8">
					<h1 className="text-3xl font-bold text-gray-900">{t('auth.forgotPassword.title')}</h1>
					<p className="mt-3 text-gray-600">
						{t(
							'auth.forgotPassword.successMessage',
							"If an account exists for that email, we've sent password reset instructions. Please check your inbox.",
						)}
					</p>

					<div className="mt-8">
						<Link
							to="/auth"
							className="block w-full text-center py-3 px-4 rounded-full font-medium text-white text-base bg-gradient-to-r from-[#225DFC] to-[#9726FA] hover:brightness-130 focus:outline-none focus:ring-2 focus:ring-[#6366F1]/50 focus:ring-offset-2 shadow-lg shadow-[#225DFC]/30 hover:shadow-xl hover:translate-y-[-1px] transition-all duration-400"
						>
							{t('auth.forgotPassword.backToSignIn', 'Back to sign in')}
						</Link>
					</div>
				</div>
			</div>
		)
	}

	return (
		<div className="w-full max-w-md mx-auto">
			<JobillaBrandLogo />

			<div className="mt-8">
				<h1 className="text-3xl font-bold text-gray-900">{t('auth.forgotPassword.title')}</h1>
				<p className="mt-2 text-gray-600">
					{t('auth.forgotPassword.description')}
				</p>
			</div>

			<form onSubmit={handleRequestReset} className="mt-8 space-y-5">
				<AuthErrorAlert message={error} />

				<AuthInput
					label={t('auth.forgotPassword.emailLabel')}
					type="email"
					placeholder={t('auth.forgotPassword.emailPlaceholder')}
					value={email}
					onChange={setEmail}
					required={true}
					icon={<MailIcon />}
				/>

				<AuthSubmitButton isLoading={forgotPassword.isPending}>
					{t('auth.forgotPassword.submit')}
				</AuthSubmitButton>

				<p className="text-center text-sm text-gray-600">
					{t('auth.forgotPassword.rememberPassword')}{' '}
					<Link to="/auth" className="text-[#6366F1] hover:text-[#4F46E5] font-medium">
						{t('auth.forgotPassword.signInLink')}
					</Link>
				</p>
			</form>
		</div>
	)
}
